import { webglTileRenderer } from '../terrain/webgl-tile-renderer.js'
import type { TowerData } from './cellular-los-worker.js'

const PROGRAM_KEY = 'cellular-los'

const FRAGMENT_SRC = `
precision highp float;
varying vec2 v_texCoord;
uniform sampler2D u_dem;
uniform sampler2D u_towers;
uniform float u_towerCount;
uniform float u_towerTexWidth;
uniform float u_paddedSize;
uniform float u_tileSize;
uniform float u_tileOffset;

const float RECEIVER_H = 2.0;
const float ANTENNA_H = 30.0;

float decodeTerrarium(vec4 c) {
  return c.r * 255.0 * 256.0 + c.g * 255.0 + c.b * 255.0 / 256.0 - 32768.0;
}

float getElev(float px, float py) {
  vec2 uv = vec2(
    (clamp(px, 0.0, u_paddedSize - 1.0) + 0.5) / u_paddedSize,
    (clamp(py, 0.0, u_paddedSize - 1.0) + 0.5) / u_paddedSize
  );
  return decodeTerrarium(texture2D(u_dem, uv));
}

vec4 towerTexel(float idx) {
  return texture2D(u_towers, vec2((idx + 0.5) / u_towerTexWidth, 0.5));
}

void main() {
  float demX = v_texCoord.x * u_tileSize + u_tileOffset;
  float demY = v_texCoord.y * u_tileSize + u_tileOffset;
  float pixelElev = getElev(demX, demY) + RECEIVER_H;

  float bits = 0.0;

  for (float i = 0.0; i < 2048.0; i += 1.0) {
    if (i >= u_towerCount) break;

    vec4 t0 = towerTexel(i * 2.0);
    vec4 t1 = towerTexel(i * 2.0 + 1.0);

    float towerPx = floor(t0.r * 255.0 + 0.5) * 256.0 + floor(t0.g * 255.0 + 0.5) - 2048.0;
    float towerPy = floor(t0.b * 255.0 + 0.5) * 256.0 + floor(t0.a * 255.0 + 0.5) - 2048.0;
    float rangePixels = floor(t1.r * 255.0 + 0.5) * 256.0 + floor(t1.g * 255.0 + 0.5);
    float carrierBit = floor(t1.b * 255.0 + 0.5);
    float margin = floor(t1.a * 255.0 + 0.5);

    float dx = towerPx - demX;
    float dy = towerPy - demY;
    float dist = sqrt(dx * dx + dy * dy);

    if (dist > rangePixels) continue;

    if (dist < 0.5) {
      float alreadySet = step(0.5, mod(floor(bits / carrierBit), 2.0));
      bits += carrierBit * (1.0 - alreadySet);
      continue;
    }

    float towerElev = getElev(towerPx, towerPy) + ANTENNA_H;
    float numSteps = min(ceil(dist), 64.0);
    float stepX = dx / numSteps;
    float stepY = dy / numSteps;

    bool blocked = false;
    for (float s = 1.0; s < 64.0; s += 1.0) {
      if (s >= numSteps) break;
      float frac = s / numSteps;
      float losH = pixelElev * (1.0 - frac) + towerElev * frac;
      float sampleElev = getElev(demX + s * stepX, demY + s * stepY);
      if (sampleElev > losH + margin) {
        blocked = true;
        break;
      }
    }

    if (!blocked) {
      float alreadySet = step(0.5, mod(floor(bits / carrierBit), 2.0));
      bits += carrierBit * (1.0 - alreadySet);
    }
  }

  gl_FragColor = vec4(bits / 255.0, 0.0, 0.0, 1.0);
}
`

const POS_OFFSET = 2048

function encodeTowerTexture(towers: TowerData[]): {
  data: Uint8ClampedArray
  width: number
} {
  const width = Math.max(towers.length * 2, 1)
  const data = new Uint8ClampedArray(width * 4)

  for (let i = 0; i < towers.length; i++) {
    const t = towers[i]
    const px = Math.round(t.demPx + POS_OFFSET)
    const py = Math.round(t.demPy + POS_OFFSET)
    const range = Math.round(t.rangePixels)

    const j = i * 8
    data[j + 0] = (px >> 8) & 0xff
    data[j + 1] = px & 0xff
    data[j + 2] = (py >> 8) & 0xff
    data[j + 3] = py & 0xff
    data[j + 4] = (range >> 8) & 0xff
    data[j + 5] = range & 0xff
    data[j + 6] = t.carrierBit
    data[j + 7] = Math.round(t.marginM)
  }

  return { data, width }
}

export function renderLOSTileGPU(
  paddedDem: Uint8ClampedArray,
  paddedSize: number,
  tileSize: number,
  tileOffset: number,
  towers: TowerData[]
): Uint8Array | null {
  if (!webglTileRenderer.available && !webglTileRenderer.init()) return null
  if (towers.length === 0) return new Uint8Array(tileSize * tileSize)

  const program = webglTileRenderer.compileProgram(FRAGMENT_SRC, PROGRAM_KEY)
  if (!program) return null

  const demTex = webglTileRenderer.uploadTexture(paddedDem, paddedSize, paddedSize)
  if (!demTex) return null

  const { data: towerData, width: towerTexWidth } = encodeTowerTexture(towers)
  const towerTex = webglTileRenderer.uploadTexture(towerData, towerTexWidth, 1)
  if (!towerTex) {
    webglTileRenderer.deleteTexture(demTex)
    return null
  }

  try {
    const result = webglTileRenderer.renderToPixels(
      program,
      {
        u_towerCount: towers.length,
        u_towerTexWidth: towerTexWidth,
        u_paddedSize: paddedSize,
        u_tileSize: tileSize,
        u_tileOffset: tileOffset,
      },
      {
        u_dem: { texture: demTex, unit: 0 },
        u_towers: { texture: towerTex, unit: 1 },
      },
      tileSize,
      tileSize
    )

    if (!result) return null

    const coverage = new Uint8Array(tileSize * tileSize)
    for (let i = 0; i < coverage.length; i++) {
      coverage[i] = result[i * 4]
    }
    return coverage
  } finally {
    webglTileRenderer.deleteTexture(demTex)
    webglTileRenderer.deleteTexture(towerTex)
  }
}
